import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/lib/utils-format";
import Seo, { orgSchema } from "@/components/Seo";

const SITE = "https://signiastylettoix.com";

const QUESTIONS = [
    { key: "lifestyle", q: "How would you describe your typical week?", options: [
        { v: "quiet", label: "Mostly quiet — home, family, TV" },
        { v: "balanced", label: "Balanced — some outings, occasional gatherings" },
        { v: "busy", label: "Busy & social — meetings, restaurants, travel" }] },
    { key: "budget", q: "What budget range feels right?", options: [
        { v: "entry", label: "Up to ₹2,00,000" },
        { v: "mid", label: "₹2,00,000 – ₹4,00,000" },
        { v: "premium", label: "Above ₹4,00,000" },
        { v: "flexible", label: "Flexible — recommend the best fit" }] },
    { key: "priority", q: "What matters most to you?", options: [
        { v: "speech_in_noise", label: "Understanding speech in noisy places" },
        { v: "discretion", label: "Nobody should notice I'm wearing it" },
        { v: "streaming", label: "Phone calls & media streaming" },
        { v: "value", label: "Best value for money" }] },
    { key: "loss", q: "Do you know your hearing loss level?", options: [
        { v: "unknown", label: "Not tested yet" },
        { v: "mild", label: "Mild" },
        { v: "moderate", label: "Moderate" },
        { v: "severe", label: "Severe" }] },
    { key: "single_sided", q: "Is the hearing loss mainly in one ear?", options: [
        { v: false, label: "No — both ears / not sure" },
        { v: true, label: "Yes — one ear only (single-sided)" }] },
];

export default function ProductFinder() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [results, setResults] = useState(null);
    const [busy, setBusy] = useState(false);

    const pick = async (key, v) => {
        const next = { ...answers, [key]: v };
        setAnswers(next);
        if (step < QUESTIONS.length - 1) { setStep(step + 1); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/product-finder", next);
            setResults(data);
        } finally { setBusy(false); }
    };

    const reset = () => { setStep(0); setAnswers({}); setResults(null); };
    const q = QUESTIONS[step];

    return (
        <>
            <Seo title="Hearing Aid Finder — Your Signia Match in 5 Questions"
                description="Answer 5 quick questions about your lifestyle, budget and priorities — get a ranked shortlist of Signia hearing aids with plain-language reasons for each."
                canonical={`${SITE}/find`} schema={{ "@context": "https://schema.org", "@graph": [orgSchema()] }} />
            <section className="pt-12 md:pt-16 pb-16">
                <div className="container-x max-w-[760px]">
                    <p className="eyebrow inline-flex items-center gap-2"><Sparkles size={12} /> Guided Product Finder</p>

                    {!results && !busy && (
                        <div data-testid="finder-question">
                            <h1 className="font-serif text-[34px] md:text-[46px] leading-[1.1] mt-4">Find Your Match in 5 Questions</h1>
                            <div className="mt-4 h-2 bg-[#e2ddd4] rounded-full overflow-hidden"><div className="h-full bg-[#003E4D] transition-all" style={{ width: `${(step / QUESTIONS.length) * 100}%` }} /></div>
                            <p className="mt-8 font-serif text-[24px] text-[#003E4D]">{step + 1}. {q.q}</p>
                            <div className="mt-5 grid gap-3">
                                {q.options.map((o, i) => (
                                    <button key={i} onClick={() => pick(q.key, o.v)} data-testid={`finder-opt-${step}-${i}`}
                                        className="text-left p-5 bg-white border border-[#e2ddd4] rounded-2xl text-[15.5px] hover:border-[#003E4D] transition">
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                            {step > 0 && <button onClick={() => setStep(step - 1)} className="mt-6 text-[13px] text-[#4a5568] underline">← Back</button>}
                        </div>
                    )}

                    {busy && <div className="min-h-[40vh] flex items-center justify-center text-[#4a5568]">Matching your answers…</div>}

                    {results && (
                        <div data-testid="finder-results">
                            <h1 className="font-serif text-[34px] md:text-[46px] leading-[1.1] mt-4">Your Shortlist</h1>
                            <p className="mt-3 text-[15px] text-[#4a5568]">{results.note}</p>
                            <div className="mt-8 space-y-5">
                                {results.results.map((p, i) => (
                                    <div key={p.slug} className="p-6 bg-white border border-[#e2ddd4] rounded-2xl flex flex-col sm:flex-row gap-5" data-testid={`finder-result-${i}`}>
                                        {p.image && <img src={p.image} alt={p.name} className="w-28 h-28 object-contain shrink-0" />}
                                        <div className="flex-1">
                                            <div className="text-[11px] uppercase tracking-wider text-[#B8860B]">{i === 0 ? "Best match" : `Match #${i + 1}`} · {p.technology_level}</div>
                                            <h2 className="font-serif text-[22px] mt-1">{p.name}</h2>
                                            <p className="text-[18px] text-[#003E4D] mt-1">{formatINR(p.price)}</p>
                                            <ul className="mt-3 space-y-1 text-[14px] text-[#4a5568] list-disc pl-5">
                                                {p.reasons.map((r, j) => <li key={j}>{r}</li>)}
                                            </ul>
                                            <div className="mt-4 flex flex-wrap gap-2">
                                                <Link to={`/products/${p.slug}`} className="btn-primary !text-[13px] !py-2 !px-4">View Details <ArrowRight size={14} /></Link>
                                                <Link to="/book-trial" className="btn-secondary !text-[13px] !py-2 !px-4">Book Free Trial</Link>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button onClick={reset} className="mt-8 inline-flex items-center gap-2 text-[13.5px] text-[#4a5568] underline" data-testid="finder-restart"><RotateCcw size={14} /> Start over</button>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
